import currencyLib from "currency.js";
import { useState } from "react";
import { useCartMutations } from "../../../../services/hooks/useCartMutations";
import { useModalHook } from "../../../../services/hooks/useModal";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../services/hooks/useReduxHooks";
import {
  removeLoyaltyOffer,
  removeOfferAction,
  setRedirectItem,
  setSelectedBuySubItem,
  setSelectedLoyaltyOfferId,
  setSelectedOfferId,
  setSelectedOfferSubItems,
} from "../../../../services/redux/slices/cartLoyaltyV2Slice";
import {
  selectActiveRedeemedOffer,
  selectCartItems,
  selectIsOfferApplied,
} from "../../../../services/redux/selectors/cartSelectors";
import { selectResolvedAppliedOffer } from "../../../../services/redux/selectors/loyaltyOfferSelectors";
import { selectUseLoyaltyService } from "../../../../services/redux/selectors/loyaltyServiceSelectors";
import { CartItemType } from "../../../../services/redux/types/CartLoyaltyV2Types";
import Offer from "../../../../utils/OfferClass/Offer/Offer";
import { isCartPointingSystemOffer } from "../../../../utils/cart/cartItemDisplay";
import { asLegacyOffer } from "../../../../utils/cart/legacyOfferDisplay";
import { DoubleLinkedList } from "../../../../utils/help/linkedList/LinkedList";
import { isArabicLanguage } from "../../../../utils/help/localization/localization";
import { INVERTED_SECTIONS } from "../../../../utils/types";

export const useCartItemList = () => {
  const isMobile = useAppSelector(
    (state) => state.globalComponentsToggleSlice.isMobile
  );

  const { renderWebViewItem } = useModalHook();
  const dispatch = useAppDispatch();
  const { removeItem } = useCartMutations();

  const franchiseConfig = useAppSelector(
    (state) => state.config.selectedFranchise?.franchiseConfig
  );
  const currency = useAppSelector((state) => state.config.currency);
  const isLanguageArabic = isArabicLanguage();

  const [disableDelete, setDisableDelete] = useState(false);

  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const isInvertedItemCart = restaurantInfo?.colors?.invertedSections?.includes(
    INVERTED_SECTIONS.ITEM_CART
  );

  const redeemedOffer = useAppSelector(selectActiveRedeemedOffer);
  const resolvedLoyaltyOffer = useAppSelector(selectResolvedAppliedOffer);
  const isOfferApplied = useAppSelector(selectIsOfferApplied);
  const cartItems = useAppSelector(selectCartItems);
  const useLoyaltyService = useAppSelector(selectUseLoyaltyService);

  const isPointingSystemOffer = isCartPointingSystemOffer({
    useLoyaltyService,
    legacyOffer: asLegacyOffer(
      useLoyaltyService ? undefined : (redeemedOffer as Offer | undefined)
    ),
    resolvedLoyaltyOffer,
  });

  const hidePrice = franchiseConfig?.forFeatureShowcase ?? false;

  const deleteItem = (itemIndex: number, lineId?: string) => {
    if (!disableDelete) {
      setDisableDelete(true);
      if (lineId) {
        removeItem({ lineId });
      } else {
        removeItem({ itemIndex });
      }
      setTimeout(() => {
        setDisableDelete(false);
      }, 100);
    }
  };

  const deleteOffer = () => {
    if (useLoyaltyService) {
      dispatch(removeLoyaltyOffer());
    } else {
      dispatch(removeOfferAction());
    }
  };

  const selectedBuyItem = cartItems.find((item) => item.isBuyItem);
  const selectedOfferItems = cartItems.filter(
    (item) => item.isOfferItem || item.isBundleItem
  );

  const handleOfferEdit = () => {
    const selectedOfferSubItems = new DoubleLinkedList<CartItemType>();

    // if (selectedOfferItems.length) {
    if (redeemedOffer instanceof Offer && redeemedOffer.isBundleOffer()) {
      selectedOfferItems
        .sort((a, b) => b.totalPrice - a.totalPrice)
        .flatMap((bundleSubitem) => {
          if (bundleSubitem.count > 1) {
            for (let i = 0; i < bundleSubitem.count; i++) {
              selectedOfferSubItems.append({
                ...bundleSubitem,
                count: 1,
                priceWithoutOffer: currencyLib(
                  bundleSubitem.priceWithoutOffer!
                ).divide(bundleSubitem.count).value,
              });
            }
          } else {
            selectedOfferSubItems.append(bundleSubitem);
          }

          return [];
        });
    } else if (redeemedOffer instanceof Offer && redeemedOffer.isBogoOffer()) {
      selectedOfferItems.forEach((item) => {
        selectedOfferSubItems.append(item);
      });
    }
    // }

    dispatch(setSelectedOfferSubItems(selectedOfferSubItems));

    if (useLoyaltyService) {
      dispatch(setSelectedLoyaltyOfferId(String(resolvedLoyaltyOffer?.id)));
    } else {
      dispatch(setSelectedOfferId((redeemedOffer as Offer)?.id));
    }

    if (redeemedOffer instanceof Offer && redeemedOffer.isBogoOffer()) {
      dispatch(setSelectedBuySubItem(selectedBuyItem));

      dispatch(
        setRedirectItem({
          itemId: selectedBuyItem?.itemDetails.id,
          subItemId: selectedBuyItem?.subItemDetails.id,
        })
      );

      return;
    }

    const head = selectedOfferSubItems.getHead();
    if (!head) {
      return;
    }
    if (useLoyaltyService) {
      dispatch(setSelectedLoyaltyOfferId(String(resolvedLoyaltyOffer?.id)));
    } else {
      dispatch(setSelectedOfferId((redeemedOffer as Offer)?.id));
    }
    dispatch(
      setRedirectItem({
        itemId: head.subItemDetails.itemId!,
        subItemId: head.subItemDetails.id,
      })
    );
  };

  return {
    isMobile,
    renderWebViewItem,
    currency,
    isLanguageArabic,
    disableDelete,
    setDisableDelete,
    isInvertedItemCart,
    redeemedOffer,
    isOfferApplied,
    isPointingSystemOffer,
    hidePrice,
    deleteItem,
    handleOfferEdit,
    deleteOffer,
  };
};
